import React, { useEffect, useRef, useState, useMemo, Suspense } from 'react'
import { useLazyGetChildAnalysisQuery } from '../../../../api/energy-analysis/calculateEnergySavingsApiSlice'
import { useDispatch } from 'react-redux'
import { showSnackbar } from '../../../../redux/snackbarSlice'
import { Box, Grid, Card, CardContent, Autocomplete, TextField, InputAdornment, Avatar, ListSubheader, ListItemAvatar, ListItemIcon, ListItemButton, Button, List, ListItem, ListItemText, Typography, IconButton, Stack, Tooltip, Chip, ToggleButtonGroup, ToggleButton } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import HtmlTooltip from '../../../../components/tooltip/HtmlTooltip';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import FunctionsRoundedIcon from '@mui/icons-material/FunctionsRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import { getTypeColor, getTypeIcon } from '../../../../utils/nodeTypeMapper';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import {
  Popper,
  Grow,
  Paper,
  MenuList,
  MenuItem,
  ClickAwayListener
} from '@mui/material';

import { DateTime } from 'luxon';

const ChildAnalysisChart = React.lazy(() => import('remote/ChildAnalysisChart').catch(() => ({ default: () => <div>Failed to load</div> })));
const EnergyAnalyzerFilterDialog = React.lazy(() => import('remote/EnergyAnalyzerFilterDialog').catch(() => ({ default: () => <div>Failed to load</div> })));

const getFilterLabel = (type, value) => {
  switch (type) {
    case 'activeHoursFilter':
      switch (value) {
        case true:
          return 'Dagstimer';
        case false:
          return 'Nattetimer';
        default:
          return 'Alle timer';
      }
    case 'activeDaysFilter':
      switch (value) {
        case true:
          return 'Hverdag';
        case false:
          return 'Weekend';
        default:
          return 'Alle dage';
      }
    case 'activeHolidaysFilter':
      switch (value) {
        case true:
          return 'Bevar helligdage';
        case false:
          return 'Udeluk helligdage';
        default:
          return '';
      }
    default:
      return '';
  }
};

const formatDate = (date) => {
  const day = date.setLocale('da').toFormat('d.');
  const month = date.setLocale('da').toFormat('MMMM');
  return `${day} ${month}`;
};



const ChildAnalysisTab = ({
  selectedAccount,
  selectedEnergyGroup,
  timeRange,
}) => {

  const dispatch = useDispatch();

  const [getChildAnalysis, { data: childAnalysisData, isLoading: isChildAnalysisLoading, isFetching: isChildAnalysisFetching, isError, error }] = useLazyGetChildAnalysisQuery();

  const [nodeToAnalyse, setNodeToAnalyse] = useState(null)
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [data, setData] = useState([]);
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [seriesColors, setSeriesColors] = useState([]);
  const [selectedRange, setSelectedRange] = useState([
    DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen'),
    DateTime.fromMillis(timeRange.end).setZone('Europe/Copenhagen'),
  ]);
  const [hiddenChildrenIds, setHiddenChildrenIds] = useState([]);
  const [dataSeriesAggregate, setDataSeriesAggregate] = useState({ value: 'AverageEnergyUsage', label: 'Gennemsnit' });
  const anchorRef = useRef(null);
  const [openAggregationMenu, setOpenAggregationMenu] = useState(false);

  const options = [
    { value: 'TotalEnergyUsage', label: 'Sum' },
    { value: 'AverageEnergyUsage', label: 'Gennemsnit' },
    { value: 'MinEnergyUsage', label: 'Minimum' },
    { value: 'MaxEnergyUsage', label: 'Maksimum' },
  ];

  const handleToggleAggregationMenu = () => {
    setOpenAggregationMenu((prev) => !prev);
  };

  const handleCloseAggregationMenu = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpenAggregationMenu(false);
  };
  const handleSelectAggregation = (option) => {
    setOpenAggregationMenu(false);
    setDataSeriesAggregate(option);
  }

  const filteredData = useMemo(() => {
    return data.map((entry) => ({
      ...entry,
      ChildNodesSummaries: entry.ChildNodesSummaries.filter(
        (child) => !hiddenChildrenIds.includes(child.Id)
      )
    }));
  }, [data, hiddenChildrenIds]);

  const handleOpenFilterDialog = () => {
    setOpenFilterDialog(true);
  }
  const handleCloseFilterDialog = () => {
    setOpenFilterDialog(false);
  }

  const fetchChildAnalysis = async ({
    start,
    end,
    filters
  }) => {
    try {
      const response = await getChildAnalysis({
        accountId: selectedAccount?.AccountId,
        energyGroupId: selectedEnergyGroup?.energyGroupId,
        nodeId: nodeToAnalyse?.energyTreeNodeId,
        start,
        end,
        ...filters,
      }).unwrap();

      return {
        ...response,
        metadata: {
          ...filters,
          id: Date.now(),
        },
      };
    } catch (err) {
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl under hentning af data.',
          severity: 'error',
        })
      );
    }
  }

  const handleApplyFilter = async (filters) => {
    const response = await fetchChildAnalysis({
      start: timeRange?.start,
      end: timeRange?.end,
      filters,
    });
    setData((prev) => [...prev, response]);
  }

  useEffect(() => {
    if (!nodeToAnalyse) return;

    const filterCombinations = [
      { activeHoursFilter: true, activeDaysFilter: true }, // Daytime + Weekday
      { activeHoursFilter: false, activeDaysFilter: true }, // Nighttime + Weekday
      { activeHoursFilter: true, activeDaysFilter: false }, // Daytime + Weekend
      { activeHoursFilter: false, activeDaysFilter: false }, // Nighttime + Weekend
    ];

    const fetchAllCombinations = async () => {
      const results = await Promise.all(
        filterCombinations.map(async (filters) => {
          const response = await fetchChildAnalysis({
            start: timeRange?.start,
            end: timeRange?.end,
            filters,
          });
          return response;
        })
      );

      // Filter out failed fetches (null/undefined) before setting
      const successfulResults = results.filter(Boolean);
      setData(successfulResults);
    };

    fetchAllCombinations();
  }, [nodeToAnalyse, timeRange]);

  useEffect(() => {
    if (!nodeToAnalyse && selectedEnergyGroup?.energyTreeNodes.length > 0) {
      setNodeToAnalyse(selectedEnergyGroup?.energyTreeNodes[0])
    }
  }, [selectedEnergyGroup])

  // Create a lookup map for quick access
  const nodeLookup = useMemo(() => {
    const map = new Map();
    selectedEnergyGroup?.energyTreeNodes?.forEach(node => {
      if (!map.has(node.energyTreeNodeId)) {
        map.set(node.energyTreeNodeId, { ...node, children: [] });
      }
      if (node.parentId) {
        if (!map.has(node.parentId)) {
          map.set(node.parentId, { children: [] });
        }
        map.get(node.parentId).children.push(node);
      }
    });

  
    return map;
  }, [selectedEnergyGroup]);

  const children = useMemo(() => nodeLookup.get(nodeToAnalyse?.energyTreeNodeId)?.children || [], [nodeToAnalyse, nodeLookup]);
  const parent = useMemo(() => nodeLookup.get(nodeToAnalyse?.parentId) || null, [nodeToAnalyse, nodeLookup]);

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={filtersOpen ? 8 : 12} xl={filtersOpen ? 9 : 12} sx={{ transition: 'flex-basis 0.3s ease' }}>
          <Card variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
            <CardContent sx={{ minHeight: 600 }}>
              <Stack
                direction="column"
                color="inherit"
                component={Card}
                elevation={0}
                sx={{
                  transition: 'box-shadow 0.3s ease',
                  height: '100%',
                  background: 'transparent',
                  mb: 4,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {true && (
                        <>
                          <Typography variant="body1" color="textSecondary" fontWeight="bold">
                            {nodeToAnalyse?.name || ''}
                          </Typography>
                          <HtmlTooltip title="Denne analyse viser, hvordan strømforbruget i en bygning fordeler sig på underliggende forbrugere under det valgte målepunkt. Forbruget vises opdelt i hverdag- og weekendtimer samt dag- og nattetimer, så du kan få indsigt i driftstider og optimeringsmuligheder." placement="right">
                            <InfoRoundedIcon fontSize="small" sx={{ color: (theme) => theme.palette.mode === 'light' ? 'lightgray' : 'gray', ml: 1 }} />
                          </HtmlTooltip>
                        </>
                      )}
                    </Box>

                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      I perioden {selectedRange[0] && selectedRange[1]
                        ? `${formatDate(selectedRange[0])} - ${formatDate(selectedRange[1])}`
                        : 'Vælg periode'}
                    </Typography>
                  </Box>

                  <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center">
                    <HtmlTooltip title={"Skift imellem aggregering"} placement="top">
                      <div>
                        <Button
                          ref={anchorRef}
                          onClick={handleToggleAggregationMenu}
                          startIcon={<FunctionsRoundedIcon />}
                          // disabled={aggregate === 'hour'}
                          sx={{
                            p: 1,
                            px: 2,
                            borderRadius: 3,
                            color: 'text.primary',
                            width: 'auto',
                            border: '1px solid',
                            borderColor: 'divider',
                            mr: 1,
                            textTransform: 'none', // optional: to keep label casing
                          }}
                        >
                          {dataSeriesAggregate?.label ?? 'Vælg'}
                        </Button>

                      </div>
                    </HtmlTooltip>

                    <Popper
                      open={openAggregationMenu}
                      anchorEl={anchorRef.current}
                      placement="bottom-end"
                      transition
                      disablePortal
                      sx={{ zIndex: 1301 }}
                    >
                      {({ TransitionProps }) => (
                        <Grow {...TransitionProps} style={{ transformOrigin: 'left top' }}>
                          <Paper elevation={0} variant="outlined" sx={{ mt: 0.5, borderRadius: 2 }}>
                            <ClickAwayListener onClickAway={handleCloseAggregationMenu}>
                              <MenuList autoFocusItem={open} dense sx={{ minWidth: 150, p: 1 }}>
                                {options.map((option) => (
                                  <MenuItem
                                    key={option.value}
                                    onClick={() => handleSelectAggregation(option)}
                                    sx={{
                                      py: '6px',
                                      px: '12px',
                                      borderRadius: '5px',
                                      '&:hover': {
                                        bgcolor: 'action.hover',
                                        transition: 'all 0.3s ease',
                                      },
                                    }}
                                  >
                                    <Box
                                      component="span"
                                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                    >
                                      <Typography variant="body2" fontWeight="medium">
                                        {option.label}
                                      </Typography>
                                    </Box>
                                  </MenuItem>
                                ))}
                              </MenuList>
                            </ClickAwayListener>
                          </Paper>
                        </Grow>
                      )}
                    </Popper>
                    <HtmlTooltip title={`${filtersOpen ? 'Skjul' : 'Vis'} filtre`} placement="top">
                      <IconButton
                        onClick={() => setFiltersOpen(!filtersOpen)}
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, ml: 4 }}
                      >
                        {filtersOpen ? <KeyboardArrowRightRoundedIcon /> : <KeyboardArrowLeftRoundedIcon />}
                      </IconButton>
                    </HtmlTooltip>

                  </Box>
                </Box>
              </Stack>
              <Suspense fallback={<div>Loading...</div>}>
                <ChildAnalysisChart
                  data={filteredData}
                  dataSeriesAggregate={dataSeriesAggregate}
                  setSeriesColors={setSeriesColors}
                  isLoading={isChildAnalysisLoading || isChildAnalysisFetching}
                />
              </Suspense>
            </CardContent>
          </Card>
        </Grid>

        {filtersOpen &&
          <Grid item xs={12} md={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body1" color="textSecondary" fontWeight="bold">
                            Serier
                          </Typography>
                          <HtmlTooltip title="Tilføj eller fjern dataserier ved at filtrere på timer, ugedage og helligdage. Det giver dig mulighed for at analysere specifikke forbrugsmønstre og sammenligne forskellige perioder." placement="top">
                            <InfoRoundedIcon fontSize="small" sx={{ color: (theme) => theme.palette.mode === 'light' ? 'lightgray' : 'gray', ml: 1 }} />
                          </HtmlTooltip>
                        </Box>
                        <Button
                          onClick={handleOpenFilterDialog}
                          variant="text"
                          endIcon={<AddRoundedIcon />}
                          disableElevation
                          sx={{
                            textTransform: 'none',
                            borderRadius: 2,
                            // backgroundColor: 'hsl(0, 0%, 20%)',
                            color: 'text.secondary',
                            '&:hover': {
                              backgroundColor: 'transparent',
                              color: 'text.primary',
                            },
                          }}
                        >
                          Tilføj ny serie
                        </Button>
                      </Box>

                      <List dense>
                        {data?.map((series, index) => {
                          const seriesColor = seriesColors[index]?.color ?? '#ccc';

                          return (
                            <ListItem
                              key={series?.metadata?.id || index}
                              sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                mb: 1,
                                alignItems: 'center',
                              }}
                              secondaryAction={
                                <IconButton
                                  edge="end"
                                  onClick={() => {
                                    const idToRemove = series?.metadata?.id;
                                    setData((prev) =>
                                      prev.filter((s) => s.metadata?.id !== idToRemove)
                                    );
                                    setSeriesColors((prev) =>
                                      prev.filter((_, i) => data[i]?.metadata?.id !== idToRemove)
                                    );
                                  }}
                                >
                                  <DeleteRoundedIcon />
                                </IconButton>
                              }
                            >
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  backgroundColor: seriesColor,
                                  mr: 2,
                                  mt: 0.5,
                                }}
                              />
                              <ListItemText
                                // primary={`${seu} - Serie ${index + 1}`}
                                primary={`Serie ${index + 1}`}
                                secondary={
                                  <Typography variant="caption" component="div">
                                    <i>Anvendte filtre: </i>
                                    {[
                                      getFilterLabel('activeDaysFilter', series?.metadata?.activeDaysFilter),
                                      getFilterLabel('activeHoursFilter', series?.metadata?.activeHoursFilter),
                                      getFilterLabel('activeHolidaysFilter', series?.metadata?.activeHolidaysFilter),
                                    ]
                                      .filter(Boolean)
                                      .map((text, i) => (
                                        <span key={i}>
                                          {i > 0 && ', '}
                                          <strong>{text}</strong>
                                        </span>
                                      ))}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    </Box>
                  </CardContent>
                </Card>

              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>

                    <Grid container spacing={2} sx={{ mb: 1, mt: 1 }}>
                      <Grid item xs>
                        <Autocomplete
                          options={selectedEnergyGroup?.energyTreeNodes || []}
                          getOptionLabel={(option) => option?.name || ""}
                          value={nodeToAnalyse || selectedEnergyGroup?.energyTreeNodes[0] || null}
                          onChange={(event, newValue) => {
                            if (newValue) setNodeToAnalyse(newValue);
                          }}
                          disableClearable
                          renderOption={(props, option) => (
                            <ListItem {...props} key={option.id}>
                              <ListItemAvatar>
                                <Avatar variant="rounded" sx={{
                                  backgroundColor: getTypeColor(option.type) || "#555",
                                  color: "#fff",
                                  fontWeight: "bold",
                                  borderRadius: 2
                                }}>
                                  <i className={`fas ${getTypeIcon(option.type)}`}></i>
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText primary={option.name} />
                            </ListItem>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Vælg målepunkt"
                              variant="outlined"
                              fullWidth
                              sx={{
                                "& .MuiInputBase-root": { borderRadius: "10px" },
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "10px",
                                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
                                },
                                "& .MuiInputLabel-root": { color: "gray" },
                              }}
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: nodeToAnalyse ? (
                                  <InputAdornment position="start">
                                    <Avatar variant="rounded" sx={{
                                      backgroundColor: getTypeColor(nodeToAnalyse.type) || "#555",
                                      color: "#fff",
                                      fontWeight: "bold",
                                      borderRadius: 2
                                    }}><i className={`fas ${getTypeIcon(nodeToAnalyse.type)}`}></i></Avatar>
                                  </InputAdornment>
                                ) : null,
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item>
                        <IconButton
                          onClick={() => parent && setNodeToAnalyse(parent)}
                          disabled={!parent}
                          sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}
                        >
                          <ArrowUpwardRoundedIcon />
                        </IconButton>
                      </Grid>
                    </Grid>

                    <List
                      dense
                      sx={{
                        mt: 2,
                        maxHeight: 425,
                        overflowY: 'auto',
                      }}
                      subheader={
                        <ListSubheader component="div" id="nested-list-subheader">
                          Neddelinger for {nodeToAnalyse?.name}:
                        </ListSubheader>
                      }
                    >
                      {children.length > 0 ? (
                        children.map((child) => {
                          const isHidden = hiddenChildrenIds.includes(child.energyTreeNodeId);
                          const hasChildren = nodeLookup.get(child?.energyTreeNodeId)?.children.length > 0;
                          return (
                            <ListItem
                              key={child.id}
                              disablePadding
                              secondaryAction={
                                <Tooltip title={isHidden ? "Vis serie i grafen" : "Skjul serie fra grafen"}>
                                  <IconButton
                                    edge="end"
                                    onClick={(e) => {
                                      e.stopPropagation(); // prevent triggering setNodeToAnalyse
                                      setHiddenChildrenIds((prev) =>
                                        isHidden
                                          ? prev.filter((id) => id !== child.energyTreeNodeId)
                                          : [...prev, child.energyTreeNodeId]
                                      );
                                    }}
                                  >
                                    {isHidden ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
                                  </IconButton>
                                </Tooltip>
                              }
                            >
                              <ListItemButton disabled={!hasChildren} onClick={() => setNodeToAnalyse(child)} sx={{ borderRadius: 2 }}>
                                <ListItemIcon>
                                  <Avatar
                                    variant="rounded"
                                    size="small"
                                    sx={{
                                      backgroundColor: getTypeColor(child.type) || "#555",
                                      color: "#fff",
                                      fontWeight: "bold",
                                      borderRadius: 2,
                                    }}
                                  >
                                    <i className={`fas ${getTypeIcon(child.type)}`} />
                                  </Avatar>
                                </ListItemIcon>
                                <ListItemText primary={child.name} secondary={child.type} />
                              </ListItemButton>
                            </ListItem>
                          );
                        })
                      ) : (
                        <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', color: 'gray' }}>
                          Ingen yderligere neddeling
                        </Typography>
                      )}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        }
      </Grid>

      <Suspense fallback={<div>Loading...</div>}>
        <EnergyAnalyzerFilterDialog open={openFilterDialog} handleClose={handleCloseFilterDialog} handleApply={handleApplyFilter} />
      </Suspense>
    </Box>
  )
}

export default ChildAnalysisTab